import { useState, useEffect } from "react";
import { t } from "i18next";
import { Input } from "@material-tailwind/react";
import { format } from "date-fns";
import DynamicListSelect from "../dynamic-list/DynamicListSelect";
import { openNativePicker } from "./openNativePicker";

/* Interfaces */
export interface PeriodSelectData {
  period: string;
  value: string; // yyyy-MM-dd for day, yyyy-Www for week, yyyy-MM for month
}

interface PeriodSelectProps {
  onChange: (data: PeriodSelectData) => void;
  className?: string;
  initialData?: Partial<PeriodSelectData>;
}
/***/

/**
* PeriodSelect: Component to select a period and date/week/month
* @param onChange - Callback on period or value change
* @param className - Additional CSS classes
* @param initialData - Optional initial period and value
*/
export default function PeriodSelect({
  onChange,
  className = "",
  initialData
}: PeriodSelectProps) {
  // Options de période définies pour une utilisation locale
  const periodOptions: Array<{value: string, label: string}> = [
    {value: "day", label: t("common:calendarViews.day")},
    {value: "week", label: t("common:calendarViews.week")},
    {value: "month", label: t("common:calendarViews.month")}
  ];

  // Trouve l'objet période initial basé sur la string initialData.period ou utilise "jour" par défaut
  const getInitialPeriodObject = () => {
    const initialPeriodValue = initialData?.period || "day";
    return periodOptions.find(opt => opt.value === initialPeriodValue) || periodOptions[0];
  };

  const [currentPeriod, setCurrentPeriod] = useState<{value: string, label: string}>(getInitialPeriodObject);
  const [selectedValue, setSelectedValue] = useState(() => {
    if (initialData?.value) return initialData.value;
    // Initialise selectedValue en fonction de la valeur de la période initiale
    switch(currentPeriod.value) {
    case "week":
      return format(new Date(), "yyyy") + "-W" + format(new Date(), "ww");
    case "month":
      return format(new Date(), "yyyy-MM");
    default: // day
      return format(new Date(), "yyyy-MM-dd");
    }
  });

  useEffect(() => {
    // Notifie le parent avec la valeur string de la période
    onChange({ period: currentPeriod.value, value: selectedValue });
  }, [currentPeriod, selectedValue, onChange]);
  
  const handleTypeChange = (selectedItem: {value: string, label: string} | {value: string, label: string}[]) => {
    // DynamicListSelect's onChange provides T | T[]. 
    // For this usage (isMulti=false), we expect T, which is {value: string, label: string}.
    if (selectedItem && !Array.isArray(selectedItem)) {
      const newPeriodObject = selectedItem as {value: string, label: string};
      setCurrentPeriod(newPeriodObject);
      
      switch(newPeriodObject.value) {
      case "week":
        setSelectedValue(format(new Date(), "yyyy") + "-W" + format(new Date(), "ww"));
        break;
      case "month":
        setSelectedValue(format(new Date(), "yyyy-MM"));
        break;
      default: // day
        setSelectedValue(format(new Date(), "yyyy-MM-dd"));
        break;
      }
    }
  };

  return (
    <div className={`flex flex-col gap-y-4 ${className}`}> 
      <div className="flex items-center gap-x-2">
        <label htmlFor="period-type-select" className="w-1/3 text-sm font-medium text-gray-700">
          {t("common:type")}
        </label>
        <div className="w-2/3">
          <DynamicListSelect<{value: string, label: string}>
            data={currentPeriod}
            ressource="period-types"
            onChange={handleTypeChange}
            placeholder={t("common:selectPeriodPlaceholder")}
            classNamePrefix="react-select"
          />
        </div>
      </div>

      <div className="flex items-center gap-x-2">
        <label htmlFor="period-value-input" className="w-1/3 text-sm font-medium text-gray-700">
          {t("technician:form.fields.exportMonth.label")}
        </label>
        <div className="w-2/3">
          {currentPeriod.value === "day" && (
            <Input
              id="period-value-input-day" 
              type="date"
              value={selectedValue}
              onClick={openNativePicker}
              onChange={(e) => setSelectedValue(e.target.value)}
              placeholder="JJ/MM/AAAA"
            />
          )}

          {currentPeriod.value === "week" && (
            <Input
              id="period-value-input-week" 
              type="week"
              value={selectedValue}
              onClick={openNativePicker}
              onChange={(e) => setSelectedValue(e.target.value)}
              placeholder="Sxx-AAAA" 
            />
          )}

          {currentPeriod.value === "month" && (
            <Input
              id="period-value-input-month" 
              type="month"
              value={selectedValue}
              onClick={openNativePicker}
              onChange={(e) => setSelectedValue(e.target.value)}
              placeholder="MM/AAAA"
            />
          )}
        </div>
      </div>
    </div>
  );
}